import {
  Fullscreen,
  FullscreenExit,
  NavigateBefore,
  NavigateNext,
  Pause,
  PlayArrow,
  Refresh,
  VolumeMute,
  VolumeUp,
  ZoomIn,
  ZoomOut,
} from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Container, Divider, IconButton, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import Hls from "hls.js";
import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { getM3U8LinkForCourse } from "../services/courseServices";
import { Colors } from "../style/theme";
import { PdfWorkerSrc } from "../utils/constants";

// Set the worker for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = PdfWorkerSrc;


// const loadPdfWorker = async () => {
//   try {
//     const workerUrl = new URL('pdf.worker.min.mjs', window.location.origin).toString();
//     pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
//   } catch (error) {
//     console.error("Failed to load PDF worker:", error);
//   }
// };
// loadPdfWorker();

const MultiFormatViewer = ({ fileUrl, fileType, preventDownload = true }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);

  // Specific state for video/audio
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [controlsTimeout, setControlsTimeout] = useState(null);

  const videoContainerRef = useRef(null);

  // HLS specific state
  const [hlsInstance, setHlsInstance] = useState(null);

  const containerRef = useRef(null);
  const mediaRef = useRef(null);

  // On file change, reset state and load the new file
  useEffect(() => {
    resetState();
    loadFile();

    // Clean up HLS instance when component unmounts or when file changes
    return () => {
      if (hlsInstance) {
        hlsInstance.destroy();
        setHlsInstance(null);
      }
    };
  }, [fileUrl, fileType]);

  // Reset the component state
  const resetState = () => {
    setLoading(true);
    setError(null);
    setCurrentPage(1);
    setTotalPages(0);
    setScale(1.0);
    setIsPlaying(false);
    setIsMuted(false);
    setDuration(0);
    setCurrentTime(0);

    // Clean up existing HLS instance
    if (hlsInstance) {
      hlsInstance.destroy();
      setHlsInstance(null);
    }
  };
  
  // Load file based on its type
  const loadFile = async () => {
    try {
      setLoading(true);
      const validFileType = ["pdf", "image", "video"];
      if (!validFileType.includes(fileType.toLowerCase())) {
        setError(`Unsupported file type: ${fileType}`);
      }
    } catch (err) {
      console.error("Error loading file:", err);
      setError(`Could not load file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((fileType.toLowerCase() === "video") && mediaRef.current) {
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 3,

          xhrSetup: async (xhr, url) => {
            try {
              let segment = "";
              if (!url.endsWith(".m3u8") && url.includes("/")) {
                segment = url.split("/").pop();
              }

              const payload = {
                baseUrl: fileUrl,
                segment: segment,
              };

              const response = await getM3U8LinkForCourse(payload);

              if (!response.success) {
                throw new Error(`Failed to get presigned URL: ${response.message || "Unknown error"}`);
              }

              xhr.open("GET", response.data, true);
              return;
            } catch (error) {
              console.error("Error fetching presigned URL:", error);
              xhr.open("GET", url, true);
            }
          },
        });

        hls.loadSource(fileUrl);
        hls.attachMedia(mediaRef.current);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setLoading(false);
          if (isPlaying) {
            mediaRef.current.play().catch((err) => {
              console.error("HLS autoplay error:", err);
            });
          }
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error("HLS network error:", data);
                hls.startLoad(); // Try to recover
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error("HLS media error:", data);
                hls.recoverMediaError(); // Try to recover
                break;
              default:
                // Cannot recover from other errors
                setError(`HLS stream error: ${data.details}`);
                hls.destroy();
                break;
            }
          }
        });

        setHlsInstance(hls);

        return () => {
          if (hls) {
            hls.destroy();
          }
        };
      } else if (mediaRef.current.canPlayType("application/vnd.apple.mpegurl")) {
        // For Safari which has native HLS support
        mediaRef.current.src = fileUrl;
        mediaRef.current.addEventListener("loadedmetadata", () => {
          setLoading(false);
        });
        mediaRef.current.addEventListener("error", () => {
          setError("Failed to load HLS stream");
          setLoading(false);
        });
      } else {
        setError("HLS is not supported in this browser");
      }
    }
  }, [fileUrl, fileType, mediaRef.current]);

    // PDF specific handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setTotalPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error("Error loading PDF:", error);
    setError(`Could not load PDF: ${error.message}`);
    setLoading(false);
  };

  // Navigation functions
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Zoom functions
  const zoomIn = () => {
    const newScale = Math.min(scale + 0.2, 3);
    setScale(newScale);
  };

  const zoomOut = () => {
    const newScale = Math.max(scale - 0.2, 0.5);
    setScale(newScale);
  };

  // Video/Audio specific handlers
  const togglePlay = () => {
    if (mediaRef.current) {
      if (isPlaying) {
        mediaRef.current.pause();
      } else {
        mediaRef.current.play().catch((err) => {
          console.error("Play error:", err);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (mediaRef.current) {
      mediaRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  //video controls
  const handleTimeUpdate = () => {
    if (mediaRef.current) {
      setCurrentTime(mediaRef.current.currentTime);
    }
  };
//video controls
  const handleLoadedMetadata = () => {
    if (mediaRef.current) {
      setDuration(mediaRef.current.duration);
      setLoading(false);
    }
  };
 //video controls
  const toggleFullScreen = () => {
    if (!videoContainerRef.current) return;

    if (!isFullScreen) {
      if (videoContainerRef.current.requestFullscreen) {
        videoContainerRef.current.requestFullscreen();
      } else if (videoContainerRef.current.webkitRequestFullscreen) {
        videoContainerRef.current.webkitRequestFullscreen();
      } else if (videoContainerRef.current.msRequestFullscreen) {
        videoContainerRef.current.msRequestFullscreen();
      }

      if (window.screen.orientation && window.screen.orientation.lock) {
        window.screen.orientation.lock("landscape").catch((err) => {
          console.log("Screen orientation lock error:", err);
        });
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }

      if (window.screen.orientation && window.screen.orientation.unlock) {
        window.screen.orientation.unlock();
      }
    }
  };
//video fullscreen useEffect
  useEffect(() => {
    const handleFullScreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;

      setIsFullScreen(Boolean(fullscreenElement));
      handleControlsVisibility();
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullScreenChange);
    document.addEventListener("mozfullscreenchange", handleFullScreenChange);
    document.addEventListener("MSFullscreenChange", handleFullScreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullScreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullScreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullScreenChange);
    };
  }, []);

  //video controls
  const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };
  //video controls
  const handleControlsVisibility = () => {
    setShowControls(true);

    if (controlsTimeout) {
      clearTimeout(controlsTimeout);
    }

    const timeout = setTimeout(() => {
      setShowControls(false);
    }, 3000);

    setControlsTimeout(timeout);
  };
 //video controls
  useEffect(() => {
    return () => {
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
    };
  }, [controlsTimeout]);

  // Prevent download methods if prevention is enabled
  useEffect(() => {
    if (!preventDownload) return;

    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    const handleKeyDown = (e) => {
      // Prevent Ctrl+S, Ctrl+P, Ctrl+Shift+S
      if ((e.ctrlKey && (e.key === "s" || e.key === "p")) || (e.ctrlKey && e.shiftKey && e.key === "s")) {
        e.preventDefault();
        return false;
      }
    };

    const element = containerRef.current;
    if (element) {
      element.addEventListener("contextmenu", handleContextMenu);
      element.addEventListener("keydown", handleKeyDown);

      return () => {
        element.removeEventListener("contextmenu", handleContextMenu);
        element.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [preventDownload, containerRef.current]);

  // Render functions for different file types
  const renderPdfViewer = () => (
    <>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "grey.100",
        }}
      >
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            sx={{ backgroundColor: Colors.primary }}
            onClick={previousPage}
            disabled={currentPage <= 1 || loading}
            size="small"
          >
            <NavigateBefore />
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: Colors.primary }}
            onClick={nextPage}
            disabled={currentPage >= totalPages || loading || totalPages === 0}
            size="small"
          >
            <NavigateNext />
          </Button>
        </Stack>

        <Typography variant="body2">{loading ? "Loading..." : totalPages > 0 ? `Page ${currentPage} of ${totalPages}` : "No PDF loaded"}</Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={zoomOut} size="small" disabled={loading || totalPages === 0}>
            <ZoomOut />
          </IconButton>
          <Typography variant="body2" sx={{ mx: 1 }}>
            {Math.round(scale * 100)}%
          </Typography>
          <IconButton onClick={zoomIn} size="small" disabled={loading || totalPages === 0}>
            <ZoomIn />
          </IconButton>
          <IconButton onClick={loadFile} size="small" title="Reload PDF">
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      <Divider />

      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          bgcolor: "grey.50",
          minHeight: "70vh",
          userSelect: "none",
          position: "relative",
          overflow: "auto",
          "@media (max-width: 900px)": {
            height: "auto",
          },
        }}
      >
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={<CircularProgress />}
          error={<Alert severity="error">Failed to load PDF</Alert>}
        >
          <Page pageNumber={currentPage} scale={scale} renderTextLayer={false} renderAnnotationLayer={false} />
        </Document>
      </Box>
    </>
  );

  const renderImageViewer = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        bgcolor: "grey.50",
        minHeight: "70vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        "@media (max-width: 900px)": {
          minHeight: "auto",
        },
      }}
    >
      <Box
        sx={{
          maxWidth: "100%",
          maxHeight: "70vh",
          overflow: "auto",
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ position: "relative" }}>
          <img  loading="lazy"
            src={fileUrl}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              transform: `scale(${scale})`,
              transformOrigin: "center center",
              transition: "transform 0.3s ease",
            }}
            onLoad={() => setLoading(false)}
            onError={() => {
              setError("Failed to load image");
              setLoading(false);
            }}
          />
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderVideoViewer = () => {
    const isVideo = fileType === "video";
    const isHls = fileType === "video";

    return (
      <>
        <Box
          ref={videoContainerRef}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "black",
            minHeight: isFullScreen ? "100vh" : "70vh",
            width: isFullScreen ? "100vw" : "100%",
            position: "relative",
            "@media (max-width:900px)": {
              minHeight: isFullScreen ? "100vh" : "100%",
            },
          }}
          onMouseMove={handleControlsVisibility}
          onTouchStart={handleControlsVisibility}
        >
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {isVideo ? (
            <video
              ref={mediaRef}
              src={isHls ? undefined : fileUrl}
              style={{
                maxWidth: "100%",
                maxHeight: isFullScreen ? "100vh" : "70vh",
                width: isFullScreen ? "100%" : undefined,
              }}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onError={(e) => {
                console.error("Video error:", e);
                setError("Failed to load video");
                setLoading(false);
              }}
              controlsList="nodownload"
              playsInline
            />
          ) : (
            <audio
              ref={mediaRef}
              src={fileUrl}
              style={{ width: "100%" }}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onError={() => {
                setError("Failed to load audio");
                setLoading(false);
              }}
              controlsList="nodownload"
            />
          )}

          {/* Overlay controls that fade in/out in both normal and fullscreen mode */}
          {isVideo && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: showControls ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(0,0,0,0.2)",
                pointerEvents: showControls ? "auto" : "none",
              }}
              onClick={(e) => {
                // Toggle play/pause on video area click
                if (e.target === e.currentTarget) {
                  togglePlay();
                  handleControlsVisibility();
                }
              }}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlay();
                  handleControlsVisibility();
                }}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.7)",
                  },
                  fontSize: "3rem",
                  padding: 2,
                }}
              >
                {isPlaying ? <Pause fontSize="inherit" /> : <PlayArrow fontSize="inherit" />}
              </IconButton>
            </Box>
          )}

          {/* Video controls bar in fullscreen */}
          {isVideo && isFullScreen && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: "rgba(0,0,0,0.7)",
                p: 1,
                opacity: showControls ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
                pointerEvents: showControls ? "auto" : "none",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton onClick={togglePlay} sx={{ color: "white" }}>
                  {isPlaying ? <Pause /> : <PlayArrow />}
                </IconButton>

                <Box sx={{ flex: 1, mx: 2 }}>
                  <input
                    type="range"
                    min={0}
                    max={duration || 100}
                    value={currentTime}
                    onChange={(e) => {
                      const time = parseFloat(e.target.value);
                      if (mediaRef.current) {
                        mediaRef.current.currentTime = time;
                        setCurrentTime(time);
                      }
                      handleControlsVisibility();
                    }}
                    style={{ width: "100%" }}
                  />
                </Box>

                <Typography variant="body2" sx={{ minWidth: 70, color: "white" }}>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </Typography>

                <IconButton onClick={toggleMute} sx={{ color: "white" }}>
                  {isMuted ? <VolumeMute /> : <VolumeUp />}
                </IconButton>

                <IconButton onClick={toggleFullScreen} sx={{ color: "white" }}>
                  <FullscreenExit />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>

        {/* Normal video controls (when not in fullscreen) */}
        {!isFullScreen && (
          <Box sx={{ bgcolor: "grey.100", p: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton onClick={togglePlay}>{isPlaying ? <Pause /> : <PlayArrow />}</IconButton>

              <Box sx={{ flex: 1, mx: 2 }}>
                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  value={currentTime}
                  onChange={(e) => {
                    const time = parseFloat(e.target.value);
                    if (mediaRef.current) {
                      mediaRef.current.currentTime = time;
                      setCurrentTime(time);
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </Box>

              <Typography variant="body2" sx={{ minWidth: 70 }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </Typography>

              <IconButton onClick={toggleMute}>{isMuted ? <VolumeMute /> : <VolumeUp />}</IconButton>

              {/* Add fullscreen button */}
              {fileType === "mp4" || fileType === "webm" || fileType === "m3u8" || fileType === "video" ? (
                <IconButton onClick={toggleFullScreen}>
                  <Fullscreen />
                </IconButton>
              ) : null}
            </Box>
          </Box>
        )}
      </>
    );
  };

  // Determine which viewer to render based on file type
  const renderViewer = () => {
    switch (fileType.toLowerCase()) {
      case "pdf":
        return renderPdfViewer();
      case "image":
        return renderImageViewer();
      case "video":
        return renderVideoViewer();
      default:
        return (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Alert severity="error">Unsupported file type: {fileType}</Alert>
          </Box>
        );
    }
  };

  // Add zoom controls for images
  const renderZoomControls = () => {
    const isImage = ["image"].includes(fileType.toLowerCase());

    if (!isImage) return null;

    return (
      <Box
        sx={{
          bgcolor: "grey.100",
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <IconButton onClick={zoomOut} size="small">
          <ZoomOut />
        </IconButton>
        <Typography variant="body2" sx={{ mx: 1 }}>
          {Math.round(scale * 100)}%
        </Typography>
        <IconButton onClick={zoomIn} size="small">
          <ZoomIn />
        </IconButton>
      </Box>
    );
  };

  return (
    <div ref={containerRef}>
      <Paper sx={{ overflow: "hidden" }}>
        {/* Main Content */}
        {loading &&
        totalPages === 0 &&
        fileType !== "video" ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "70vh",
              "@media (max-width: 900px)": {
                minHeight: "auto",
              },
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Alert severity="error">{error}</Alert>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={loadFile} startIcon={<Refresh />}>
              Try Again
            </Button>
          </Box>
        ) : (
          renderViewer()
        )}

        {/* Zoom controls for images */}
        {renderZoomControls()}

        <Divider />
      </Paper>
    </div>
  );
};

export default MultiFormatViewer;
